/**
 * Mimic Grafanas time range logic see:
 * https://grafana.com/docs/grafana/latest/reference/timerange/#time-units
 *
 * The following time units are supported: s (seconds), m (minutes), h (hours), d (days), w (weeks), M (months), y (years).
 * If you wish to display the full period of the unit (day, week, month, etc…), append /$unit to the end.
 * The minus operator allows you to step back in time, relative to now
 * Take a look at some examples to see these concepts in practice:
 *
 * Last 5 minutes = now-5m - now
 * The day so far = now/d - now
 * This week = now/w - now/w
 * This week so far = now/w - now
 * Previous Month = now-1M/M - now-1M/M
 */

const moment = require('moment');
moment.locale('de');

/**
 * parseTimeRangeKey
 *
 * Takes a string like "now-3d/2w" and returns an object with:
 * 3 as subtrahend
 * d as subtrahendUnit
 * 2 as periodLength
 * w as periodUnit
 */
export const parseTimeRangeKey = (key) => {
  if (!key) {
    return null;
  }

  const matches = (key.match(/^(now)(?:([-+]{1})([0-9]+)([mhdwMy]))?(?:\/([0-9]*?)([mhdwMy]+))?$/) || []).slice(2);
  if (matches.length <= 0) {
    return null;
  }

  return {
    // range can be in the future of now
    subtrahend: (matches[0] === '-' ? 1 : -1) * parseInt(matches[1], 10) || 0,
    subtrahendUnit: matches[2] || 'm',
    periodLength: matches[3],
    periodUnit: matches[4],
  };
};

/**
 * getTimeArrayFromRangeKey
 *
 * Returns an array of two unix timestamps
 * for the start and end date of a time range
 *
 * @param {string} key - Time range keys like "now-5m", "now-2d/w" etc.
 * @param {boolean} soFar - If true returns the current time as end date
 * @return {number[]} Array of two unix timestamps [start, end]
 */
export const getTimeArrayFromRangeKey = (key, soFar = false) => {
  const options = parseTimeRangeKey(key);
  if (options === null) {
    throw new Error(`Invalid time range key: ${key}`);
  }

  const periodUnitNameMap = {
    h: 'hour',
    d: 'day',
    w: 'week',
    M: 'month',
    y: 'year',
  };

  const range = [moment(), moment().subtract(options.subtrahend, options.subtrahendUnit)];
  const applyFn = options.subtrahend < 0 ? ['startOf', 'endOf'] : ['endOf', 'startOf'];
  const useSoFar = (Number(soFar) * options.subtrahend || Number(soFar)) > 0;

  // extend range from 00 seconds to 59 seconds to always show full the minute
  range[0] = range[0][applyFn[0]]('minute');
  range[1] = range[1][applyFn[1]]('minute');

  if (options.periodUnit) {
    range[1] = range[1].clone()[applyFn[1]](periodUnitNameMap[options.periodUnit]);
    range[0] = useSoFar ? range[0] : range[1].clone()[applyFn[0]](periodUnitNameMap[options.periodUnit]);
  }

  return range.map((time) => time.unix()).sort();
};
